import { useState, useEffect } from 'react';
import api from '../../config/axios';
import { MdAdd, MdEdit, MdDelete, MdClose, MdCheck } from 'react-icons/md';
import { useLanguage } from '../../context/LanguageContext';

const DepartmentSettings = () => {
  const { t } = useLanguage();
  const [departments, setDepartments] = useState([]);
  const [newDepartment, setNewDepartment] = useState('');
  const [companyId, setCompanyId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [editingDepartment, setEditingDepartment] = useState(null);
  const [editedName, setEditedName] = useState('');
  const [popup, setPopup] = useState({
    show: false,
    title: '',
    message: '',
    type: 'confirm', // 'confirm', 'success', 'error'
    onConfirm: null,
    showCancel: true
  });
  const [showForm, setShowForm] = useState(false);

  // Şirket ID'sini ve mevcut departmanları yükle
  useEffect(() => {
    const userStr = localStorage.getItem('user');
    if (userStr) {
      const userData = JSON.parse(userStr);
      setCompanyId(userData.companyId);
      fetchDepartments(userData.companyId);
    }
  }, []);

  // Departmanları getir
  const fetchDepartments = async (companyId) => {
    try {
      const response = await api.get(`/departments/${companyId}`);
      if (response.data.success) {
        setDepartments(response.data.departments);
      } else {
        alert(response.data.message);
      }
    } catch (error) {
      console.error('Departman yükleme hatası:', error);
      alert(error.response?.data?.message || 'Departmanlar yüklenirken bir hata oluştu');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!newDepartment.trim()) return;

    try {
      const response = await api.post('/departments', {
        department_name: newDepartment.trim(),
        company_id: companyId
      });

      if (response.data.success) {
        setDepartments([response.data.department, ...departments]);
        setNewDepartment('');
        setShowForm(false);
        showPopup({
          title: t('common.success'),
          message: t('departmentSettings.departmentCreated', { name: newDepartment.trim() }),
          type: 'success',
          showCancel: false,
          onConfirm: closePopup
        });
      }
    } catch (error) {
      console.error('Departman oluşturma hatası:', error);
      showPopup({
        title: t('common.error'),
        message: error.response?.data?.message || t('departmentSettings.createError'),
        type: 'error',
        showCancel: false,
        onConfirm: closePopup
      });
    }
  };

  const showPopup = (options) => {
    setPopup({
      show: true,
      title: options.title || 'Onay',
      message: options.message,
      type: options.type || 'confirm',
      onConfirm: options.onConfirm,
      showCancel: options.showCancel ?? true
    });
  };

  const closePopup = () => {
    setPopup(prev => ({ ...prev, show: false }));
  };

  // Silme işlemi için
  const handleDelete = async (departmentId, departmentName) => {
    showPopup({
      title: t('departmentSettings.deleteDepartment'),
      message: t('departmentSettings.deleteConfirm', { name: departmentName }),
      type: 'confirm',
      onConfirm: async () => {
        try {
          const response = await api.delete(
            `/departments/${departmentId}?company_id=${companyId}`
          );
          
          if (response.data.success) {
            setDepartments(departments.filter(dept => dept.id !== departmentId));
            showPopup({
              title: t('common.success'),
              message: t('departmentSettings.departmentDeleted', { name: departmentName }),
              type: 'success',
              showCancel: false,
              onConfirm: closePopup
            });
          }
        } catch (error) {
          console.error('Departman silme hatası:', error);
          showPopup({
            title: t('common.error'),
            message: error.response?.data?.message || t('departmentSettings.deleteError'),
            type: 'error',
            showCancel: false,
            onConfirm: closePopup
          });
        }
      }
    });
  };

  // Yeniden adlandırma işlemi için
  const handleRename = (department) => {
    setEditingDepartment(department.id);
    setEditedName(department.department_name);
  };

  const handleSaveRename = async (departmentId) => {
    try {
      const response = await api.put(`/departments/${departmentId}`, {
        department_name: editedName,
        company_id: companyId
      });

      if (response.data.success) {
        setDepartments(departments.map(dept =>
          dept.id === departmentId ? { ...dept, department_name: editedName } : dept
        ));
        setEditingDepartment(null);
        showPopup({
          title: t('common.success'),
          message: t('departmentSettings.departmentUpdated', { name: editedName }),
          type: 'success',
          showCancel: false,
          onConfirm: closePopup
        });
      }
    } catch (error) {
      showPopup({
        title: t('common.error'),
        message: error.response?.data?.message || t('departmentSettings.updateError'),
        type: 'error',
        showCancel: false,
        onConfirm: closePopup
      });
    }
  };



  // Form açıldığında input'a focus için useEffect ekleyin
  useEffect(() => {
    if (showForm) {
      // Kısa bir timeout ile input'un render olmasını bekleyelim
      const timer = setTimeout(() => {
        const input = document.querySelector('input[name="department_name"]');
        if (input) input.focus();
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [showForm]);

  if (loading) {
    return <div className="p-6">{t('common.loading')}</div>;
  }

  return (
    <div className="h-full overflow-auto">
      <div className="flex justify-between items-center gap-3 mb-6">
        <h1 className="text-xl sm:text-2xl font-medium text-gray-800 dark:text-white">{t('settings.departmentSettings')}</h1>
        <button
          onClick={() => {
            setNewDepartment('');
            setShowForm(true);
          }}
          className="flex items-center justify-center gap-2 px-3 py-2.5 bg-[#272c39] text-white rounded-md hover:bg-[#1e2230] transition-colors w-11 h-10 sm:w-auto sm:h-auto sm:px-6 sm:py-3 -ml-2.5 sm:ml-0"
        >
          <MdAdd size={20} className="sm:hidden" />
          <MdAdd size={24} className="hidden sm:block" />
          <span className="hidden sm:inline">{t('departmentSettings.addNewDepartment')}</span>
        </button>
      </div>

      {/* Total Count */}
      <div className="mb-4">
        <span className="text-sm text-gray-600 dark:text-gray-400">
          {t('departmentSettings.totalDepartments', { count: departments.length })}
        </span>
      </div>

      {/* Departman Ekleme Modal Formu */}
      {showForm && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[9000] flex items-center justify-center p-2 sm:p-4">
          <div className="relative mx-auto w-full max-w-2xl shadow-lg rounded-md bg-white dark:bg-gray-800 overflow-hidden">
            {/* Header */}
            <div className="bg-[#272c39] text-white px-3 sm:px-6 py-3 sm:py-4 flex justify-between items-center">
              <h3 className="text-sm sm:text-lg font-medium">{t('departmentSettings.addNewDepartment')}</h3>
              <button
                onClick={() => {
                  setShowForm(false);
                  setNewDepartment('');
                }}
                className="text-white hover:text-gray-200"
              >
                <MdClose size={20} />
              </button>
            </div>
            
            <div className="p-4 sm:p-8 bg-white dark:bg-gray-800">
              <form onSubmit={handleSubmit} className="space-y-4 sm:space-y-6">
                <div>
                  <label className="block text-xs sm:text-sm font-medium text-gray-700 dark:text-gray-300 mb-1 sm:mb-2">
                    {t('departmentSettings.departmentName')}
                  </label>
                  <input
                    type="text"
                    name="department_name"
                    value={newDepartment}
                    onChange={(e) => setNewDepartment(e.target.value)}
                    placeholder={t('departmentSettings.enterDepartmentName')}
                    className="w-full px-2 sm:px-3 py-1.5 sm:py-2 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm"
                    required
                  />
                </div>

                <div className="flex flex-col sm:flex-row justify-end gap-2 sm:gap-3">
                  <button
                    type="button"
                    onClick={() => {
                      setShowForm(false);
                      setNewDepartment('');
                    }}
                    className="px-3 sm:px-4 py-1.5 sm:py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 rounded-md hover:bg-gray-200 dark:hover:bg-gray-500 text-sm sm:text-base"
                  >
                    {t('common.cancel')}
                  </button>
                  <button
                    type="submit"
                    className="px-3 sm:px-4 py-1.5 sm:py-2 bg-[#272c39] text-white rounded-md hover:bg-[#1e2230] text-sm sm:text-base"
                  >
                    {t('departmentSettings.addDepartment')}
                  </button>
                </div>
              </form>
            </div>
          </div>
        </div>
      )}

      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm border border-gray-200 dark:border-gray-700 rounded-b-lg">

        {/* Desktop Table View */}
        <div className="hidden lg:block overflow-x-auto">
          <table className="w-full min-w-[600px] table-auto">
            <thead className="bg-gray-50 dark:bg-gray-700 sticky top-0 z-10">
              <tr className="bg-gray-50 dark:bg-gray-700 border-b border-gray-200 dark:border-gray-700 shadow-sm">
                <th className="px-4 py-2 text-left text-sm font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider bg-gray-50 dark:bg-gray-700 rounded-tl-lg">{t('departmentSettings.departmentName')}</th>
                <th className="px-4 py-2 text-right text-sm font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider bg-gray-50 dark:bg-gray-700 rounded-tr-lg">{t('table.actions')}</th>
              </tr>
            </thead>
            <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
              {departments.length > 0 ? (
                departments.map((dept, index) => {
                  const isLast = index === departments.length - 1;
                  return (
                    <tr key={dept.id}>
                      {/* İlk hücre: Eğer son satırsa sol alt köşe yuvarlatılır */}
                      <td className={`px-4 py-2 whitespace-nowrap text-sm text-gray-900 dark:text-white ${isLast ? 'rounded-bl-lg' : ''}`}>
                        {editingDepartment === dept.id ? (
                          <input
                            type="text"
                            value={editedName}
                            onChange={(e) => setEditedName(e.target.value)}
                            className="w-full px-3 py-1 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                            autoFocus
                          />
                        ) : (
                          <span className="text-gray-700 dark:text-gray-300">{dept.department_name}</span>
                        )}
                      </td>
                      {/* Son hücre: Eğer son satırsa sağ alt köşe yuvarlatılır */}
                      <td className={`px-4 py-2 whitespace-nowrap text-sm text-right font-medium text-gray-900 dark:text-white ${isLast ? 'rounded-br-lg' : ''}`}>
                        <div className="flex items-center justify-end gap-2">
                          {editingDepartment === dept.id ? (
                            <>
                              <button
                                onClick={() => handleSaveRename(dept.id)}
                                className="text-green-600 hover:text-green-900"
                              >
                                <MdCheck size={20} />
                              </button>
                              <button
                                onClick={() => setEditingDepartment(null)}
                                className="text-gray-600 dark:text-gray-400 hover:text-gray-900"
                              >
                                <MdClose size={20} />
                              </button>
                            </>
                          ) : (
                            <>
                              <button
                                onClick={() => handleRename(dept)}
                                className="text-blue-600 hover:text-blue-900"
                              >
                                <MdEdit size={20} />
                              </button>
                              <button
                                onClick={() => handleDelete(dept.id, dept.department_name)}
                                className="text-red-600 hover:text-red-900"
                              >
                                <MdDelete size={20} />
                              </button>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })
              ) : (
                <tr>
                  <td colSpan="2" className="px-4 py-8 rounded-bl-lg rounded-br-lg">
                    <div className="flex justify-center items-center">
                      <p className="text-gray-400 dark:text-gray-500 text-sm">{t('departmentSettings.noDepartments')}</p>
                    </div>
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        {/* Mobile Card View */}
        <div className="lg:hidden space-y-4 p-4">
          {departments.length > 0 ? (
            departments.map((dept, index) => (
              <div key={dept.id} className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 space-y-3">
                {/* Department Name */}
                <div>
                  <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('departmentSettings.departmentName')}</div>
                  {editingDepartment === dept.id ? (
                    <input
                      type="text"
                      value={editedName}
                      onChange={(e) => setEditedName(e.target.value)}
                      className="w-full px-3 py-1 border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                      autoFocus
                    />
                  ) : (
                    <div className="text-sm text-gray-900 dark:text-white font-medium">{dept.department_name}</div>
                  )}
                </div>

                {/* Actions */}
                <div>
                  <div className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t('table.actions')}</div>
                  <div className="flex items-center gap-2">
                    {editingDepartment === dept.id ? (
                      <>
                        <button
                          onClick={() => handleSaveRename(dept.id)}
                          className="text-green-600 hover:text-green-900"
                        >
                          <MdCheck size={20} />
                        </button>
                        <button
                          onClick={() => setEditingDepartment(null)}
                          className="text-gray-600 dark:text-gray-400 hover:text-gray-900"
                        >
                          <MdClose size={20} />
                        </button>
                      </>
                    ) : (
                      <>
                        <button
                          onClick={() => handleRename(dept)}
                          className="text-blue-600 hover:text-blue-900"
                        >
                          <MdEdit size={20} />
                        </button>
                        <button
                          onClick={() => handleDelete(dept.id, dept.department_name)}
                          className="text-red-600 hover:text-red-900"
                        >
                          <MdDelete size={20} />
                        </button>
                      </>
                    )}
                  </div>
                </div>
              </div>
            ))
          ) : (
            <div className="text-center py-8">
              <p className="text-gray-400 dark:text-gray-500 text-sm">{t('departmentSettings.noDepartments')}</p>
            </div>
          )}
        </div>

      </div>

      {/* Genel Popup */}
      {popup.show && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-[9000] flex items-center justify-center">
          <div className="relative mx-auto w-[600px] shadow-lg rounded-md bg-white dark:bg-gray-800 overflow-hidden">
            {/* Header */}
            <div className="bg-[#272c39] text-white px-6 py-4 flex justify-between items-center">
              <h3 className="text-lg font-medium">{popup.title}</h3>
            </div>
            
            <div className="p-8 bg-white dark:bg-gray-800">
              <div className="text-center">
                <p className="text-base text-gray-600 dark:text-gray-400 mb-6">
                  {popup.message}
                </p>
                <div className="flex justify-center gap-4">
                  {popup.showCancel && (
                    <button
                      onClick={closePopup}
                      className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-600 rounded-md hover:bg-gray-200 dark:hover:bg-gray-500"
                    >
                      {t('common.cancel')}
                    </button>
                  )}
                  <button
                    onClick={() => {
                      if (popup.onConfirm) {
                        popup.onConfirm();
                      } else {
                        closePopup();
                      }
                    }}
                    className={`px-4 py-2 text-white rounded-md ${
                      popup.type === 'error' 
                        ? 'bg-red-600 hover:bg-red-700'
                        : popup.type === 'success'
                        ? 'bg-green-600 hover:bg-green-700'
                        : 'bg-[#272c39] hover:bg-[#1e2230]'
                    }`}
                  >
                    {popup.type === 'confirm' ? t('common.confirm') : t('common.ok')}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DepartmentSettings; 